import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

import * as Api from "@modules/api";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { translate } from "@modules/localizations/translate";
import {
  clearProductGroupByPromoCode,
  getFeaturesByPlanId,
  getProductGroupByPromoCode,
  getProductGroupDefault,
} from "@lib/stores";
import {
  useGetFeaturesByPlanId,
  useGetProductGroupByPromoCode,
  useGetProductGroupDefault,
} from "@lib/stores/payment/hooks";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";

import { Product } from "./Product";

interface ProductsProps {
  onProductSelect: (
    product: Api.Server.ProductGroupProductOutput,
    promoCode?: string
  ) => void;
}

let productGroupDefaultFetched = false;

export const Products = ({ onProductSelect }: ProductsProps) => {
  const [promoCode, setPromoCode] = useState("");
  const [isPromoCodeActive, setIsPromoCodeActive] = useState<
    boolean | undefined
  >(undefined);
  const [selectedProductId, setSelectedProductId] = useState<
    number | undefined
  >(undefined);

  const { data: productGroupDefaultData, status: productGroupDefaultStatus } =
    useGetProductGroupDefault();
  const {
    data: productGroupByPromoCodeData,
    status: productGroupByPromoCodeStatus,
  } = useGetProductGroupByPromoCode();
  const { data: featuresData, status: featuresStatus } =
    useGetFeaturesByPlanId();

  const products = productGroupByPromoCodeData
    ? productGroupByPromoCodeData.products
    : productGroupDefaultData?.products;

  const isInitialLoading = useIsLoadingVisibile(
    productGroupDefaultStatus === "loading" ||
      (featuresStatus === "loading" && !featuresData),
    undefined,
    0
  );

  const onPromoCodeSubmit = () => {
    getProductGroupByPromoCode(
      { promoCode },
      (productGroup) => {
        setIsPromoCodeActive(true);

        if (productGroup.planId) {
          getFeaturesByPlanId(productGroup.planId);
        }
      },
      () => {
        setIsPromoCodeActive(false);

        clearProductGroupByPromoCode();
      }
    );
  };

  useEffect(() => {
    if (!productGroupDefaultFetched) {
      productGroupDefaultFetched = true;

      getProductGroupDefault((productGroup) => {
        if (productGroup.planId) {
          getFeaturesByPlanId(productGroup.planId);
        }
      });
    }
  }, []);

  const renderProducts = () => {
    return products?.map((p) => {
      return (
        <div key={p.id} className="h-[89px]">
          <Product
            data={p}
            isSelected={p.id === selectedProductId}
            action={() => {
              setSelectedProductId(p.id);
            }}
          />
        </div>
      );
    });
  };

  const renderFeatures = () => {
    return featuresData.map((f) => {
      return (
        <div
          key={f.id}
          className="mt-[28px] flex items-center justify-start gap-x-[9px]"
        >
          <Check className="dark:text-dark-primary text-primary" />
          <div className="text-[20px] font-medium leading-[20px] text-black dark:text-white">
            {f.name}
          </div>
        </div>
      );
    });
  };

  return productGroupDefaultStatus !== "loading" && !isInitialLoading ? (
    <div>
      <div className="relative w-fit">
        <h1 className="w-fit text-[30px] font-medium leading-[36px] text-black dark:text-white">
          <TranslatedText text="subscription.titles.get premium" />
        </h1>
        <div className="dark:bg-dark-primary absolute -bottom-[8px] h-[5px] w-[146px] bg-primary ltr:left-0 rtl:right-0"></div>
      </div>
      <div className="mt-[28px]">{renderFeatures()}</div>
      <div className="relative mt-[66px] flex h-full w-full items-center justify-center gap-x-5">
        <div
          className={`relative h-[48px] w-[260px] overflow-hidden rounded-lg border-[1px] outline-none ${isPromoCodeActive === false ? "border-[#FF0000]" : "dark:border-dark-primary border-primary"}`}
        >
          {promoCode && (
            <div className="absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black">
              <X
                size={13}
                onClick={() => {
                  setPromoCode("");
                  setIsPromoCodeActive(undefined);

                  clearProductGroupByPromoCode();

                  if (productGroupDefaultData?.planId) {
                    getFeaturesByPlanId(productGroupDefaultData.planId);
                  }
                }}
              />
            </div>
          )}
          <input
            value={promoCode}
            placeholder={translate("subscription.placeholders.promo code")}
            className={`${promoCode ? "pr-[34px]" : "pr-[15px]"} flex h-full w-full items-center justify-center border-none bg-white text-[20px] font-normal outline-none placeholder:h-[31px] placeholder:text-[16px] placeholder:text-[#DADADA] dark:bg-dark_background ${isPromoCodeActive === false ? "text-[#FF0000]" : "text-black dark:text-white"}`}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPromoCode(event.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && promoCode) {
                onPromoCodeSubmit();
              }
            }}
          />
          {isPromoCodeActive ? (
            <div
              className={`opacity-0 ${
                isPromoCodeActive && "opacity-100"
              } absolute left-[14px] top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-[#13B253] bg-[#ECF9F2] transition-all`}
            >
              <div className="text-[#13B253]">
                <Check size={20} />
              </div>
            </div>
          ) : isPromoCodeActive === false ? (
            <div
              className={`opacity-0 ${
                isPromoCodeActive === false && "opacity-100"
              } absolute left-[14px] top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-[#FF0000] bg-[#ECF9F2] transition-all`}
            >
              <div className="text-[#FF0000]">
                <X size={18} />
              </div>
            </div>
          ) : null}
        </div>
        <div className="h-[48px] w-[132px]">
          <ShadcnButton
            disabled={!promoCode}
            className="!block h-full w-full bg-[#F5F5F5] px-6 text-white hover:bg-[#F5F5F5] dark:bg-white"
            onClick={onPromoCodeSubmit}
          >
            <TranslatedText
              text={`subscription.buttons.${"activate the code"}`}
              className="dark:text-dark-primary text-[20px] font-medium leading-[30px] tracking-[-1.1%] text-primary/80"
            />
          </ShadcnButton>
        </div>
      </div>
      <div className="mx-auto mt-[62px] w-[358px]">
        {productGroupByPromoCodeStatus !== "loading" ? (
          renderProducts()
        ) : (
          <div className="flex h-[150px] items-center justify-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-[2px] border-y-black border-l-transparent border-r-black dark:border-y-white dark:border-r-white"></div>
          </div>
        )}
      </div>
      <div className="mx-auto mt-[23px] w-[358px]">
        <div className="h-[50px] w-full">
          <ShadcnButton
            disabled={!selectedProductId}
            className="!block h-full w-full text-white"
            onClick={() => {
              const product = products?.find((p) => p.id === selectedProductId);

              product && onProductSelect(product, promoCode);
            }}
          >
            <TranslatedText
              text={`subscription.buttons.${"subscribe"}`}
              className="text-[20px] font-bold leading-[28px] text-white dark:text-dark_background"
            />
          </ShadcnButton>
        </div>
      </div>
      <div className="mt-[29px] pb-10">
        <TranslatedText
          text={`subscription.titles.${"cancel subscription"}`}
          className="text-center text-[12px] font-normal text-[#0C0C0C]/50 dark:text-[#979797]"
        />
      </div>
    </div>
  ) : (
    <div className="flex h-full items-center justify-center">
      <div className="h-14 w-14 animate-spin rounded-full border-[3px] border-y-black border-l-transparent border-r-black dark:border-y-white dark:border-r-white"></div>
    </div>
  );
};
